import { Link } from "react-router-dom";
import { ArrowRight, Heart, Flag, CircleCheck } from "lucide-react";
import { MaskedLines, FadeIn, ParallaxImage } from "@/components/motion";
import { Marquee } from "@/components/Marquee";
import { PROGRAMS, usePageMeta } from "@/lib/site";

const Programs = () => {
  usePageMeta(
    "Programs — Hope Centers International",
    "Feeding Centers and Dream Release: how Hope Centers feeds and educates children in the Philippines and Lithuania."
  );

  return (
    <div data-testid="programs-page">
      <section className="pt-32 md:pt-44 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn y={16}>
            <p className="label">Our programs</p>
          </FadeIn>
          <h1
            data-testid="programs-heading"
            className="mt-6 font-serif font-medium text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] tracking-tighter leading-[0.98]"
          >
            <MaskedLines
              delay={0.15}
              lines={[<>Two ways we stand</>, <>in the <em className="text-terra">gap.</em></>]}
            />
          </h1>
          <FadeIn delay={0.5} className="mt-8 max-w-xl">
            <p className="text-base sm:text-lg leading-relaxed text-ink/75">
              One feeds the body, the other feeds the future. Both are run by local leaders, rooted in the
              neighborhood, and open to every child — always.
            </p>
          </FadeIn>
        </div>
      </section>

      <Marquee items={["Feed", "Teach", "Mentor", "Stay", "Grow"]} />

      {PROGRAMS.map((p, i) => (
        <section
          key={p.slug}
          id={p.slug}
          data-testid={`program-section-${p.slug}`}
          className="py-20 md:py-32 scroll-mt-24"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
              <FadeIn className={`md:col-span-5 ${i % 2 === 1 ? "md:order-2 md:col-start-8" : ""}`}>
                <ParallaxImage
                  src={p.image}
                  alt={p.alt}
                  className={`rounded-2xl ${i % 2 === 1 ? "aspect-[4/3]" : "aspect-[4/3] md:mt-16"}`}
                  speed={0.08}
                />
              </FadeIn>
              <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-1 md:col-start-1" : "md:col-start-7"}`}>
                <FadeIn>
                  <span
                    className="font-serif text-7xl md:text-8xl font-semibold leading-none"
                    style={{ WebkitTextStroke: "1.5px #EC4849", color: "transparent" }}
                  >
                    {p.number}
                  </span>
                  <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-medium tracking-tight">{p.name}</h2>
                  <p className="mt-3 font-serif italic text-2xl text-terra">{p.tagline}</p>
                  <p className="mt-6 text-base sm:text-lg leading-relaxed text-ink/75">{p.intro}</p>
                  <p className="mt-4 text-base leading-relaxed text-ink/60">{p.body}</p>
                </FadeIn>

                <FadeIn delay={0.1}>
                  <p className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-ink-soft">
                    {p.impactsTitle || "What your gift does"}
                  </p>
                  <ul className="mt-4 space-y-3">
                    {p.impacts.map((imp) => (
                      <li key={imp.amount} className="flex items-start gap-3 text-ink/80">
                        <CircleCheck size={20} className="mt-0.5 shrink-0 text-terra" />
                        <span>
                          <strong className="font-bold">{imp.amount}</strong> — {imp.note}
                        </span>
                      </li>
                    ))}
                  </ul>
                </FadeIn>

                <FadeIn delay={0.15}>
                  <div className="mt-10 flex flex-wrap gap-8 border-t border-ink/10 pt-8">
                    {p.stats.map((s) => (
                      <div key={s.label}>
                        <p className="font-serif text-4xl font-medium tracking-tight text-terra">{s.value}</p>
                        <p className="mt-1 text-xs text-ink-soft">{s.label}</p>
                      </div>
                    ))}
                  </div>
                  <Link
                    to={`/donate?program=${p.slug}`}
                    data-testid={`program-donate-button-${p.slug}`}
                    className="btn-primary group mt-10"
                  >
                    Give to {p.name}
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </FadeIn>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* MORE WAYS TO HELP */}
      <section className="py-24 md:py-32 bg-paper-deep border-y border-ink/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn className="mb-14">
            <p className="label">Beyond giving</p>
            <h2 className="mt-4 font-serif font-medium text-4xl sm:text-5xl tracking-tight leading-[1.08]">
              More ways to help.
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: Heart,
                title: "Give monthly",
                text: "Become a Hope Partner. Steady monthly gifts let centers plan meals and semesters ahead.",
                to: "/donate",
                testid: "help-monthly",
              },
              {
                icon: Flag,
                title: "Golf Scramble 2026",
                text: "Tee off for the kids on Aug 29 at NMSU Golf Course — our biggest fundraiser of the year.",
                to: "/golf-scramble",
                testid: "help-golf",
              },
            ].map((c, i) => (
              <FadeIn key={c.title} delay={i * 0.1}>
                <Link
                  to={c.to}
                  data-testid={`help-card-${c.testid}`}
                  className="group block h-full bg-white border border-ink/10 rounded-2xl p-8 transition-[border-color,transform] duration-500 hover:border-terra/40 hover:-translate-y-1"
                >
                  <span className="grid place-items-center w-12 h-12 rounded-full bg-terra/10 text-terra">
                    <c.icon size={20} />
                  </span>
                  <h3 className="mt-6 font-serif text-2xl md:text-3xl font-medium tracking-tight">{c.title}</h3>
                  <p className="mt-3 text-ink/65 leading-relaxed">{c.text}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-terra">
                    Get involved
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Programs;
